import { DailyConsumption } from "../types";
import { daysInMonth, parseMonth } from "./months";

/** Pivoted per-day row used by the stacked-bar recharts chart.
 *
 * One row per day of the selected month, with one numeric field per
 * model_core (the credit consumption for that core on that day). Days with no
 * activity render as zero-valued bars (still in the domain so the X-axis
 * spacing is uniform).
 *
 * `_byCore` carries the per-core requests/tokens/credits map for the day —
 * used by the custom tooltip and stripped from the recharts data props.
 */
export interface DailyChartRow {
  date: string; // YYYY-MM-DD
  dayLabel: string; // "1", "8", "15", ...
  [model_core: string]: number | string | DailyCoreBreakdown;
  _byCore: DailyCoreBreakdown;
}

export type DailyCoreBreakdown = Record<
  string,
  { requests: number; tokens: number; credits: number }
>;

/** Build per-day chart rows from the backend's flat (date, model_core) list.
 *
 * - Always emits one row per calendar day of the selected month, including
 *   future days of the current month. Future/empty days carry all-zero core
 *   values (zero-height bars) so the X-axis band width stays constant — early
 *   in the month the rendered bars are the same width as late in the month,
 *   rather than spreading out across the container (issue #1215).
 * - For each (date, model_core) tuple with data, sets the per-core credit
 *   value on the row.
 * - For days with no data, the per-core fields are absent and `_byCore` is
 *   empty — recharts treats missing keys as zero-height bars.
 *
 * `cores` is the full list of model_cores to include as stack layers, in
 * stable order. Pass the keys of `usage_by_model_core` so the chart and the
 * table use the same set.
 */
export function transformDailyConsumption(
  rows: DailyConsumption[],
  selectedMonth: string,
  cores: string[],
): DailyChartRow[] {
  const { year, month } = parseMonth(selectedMonth);
  const total = daysInMonth(selectedMonth);

  // Bucket rows by date.
  const byDate = new Map<string, DailyConsumption[]>();
  for (const r of rows) {
    const arr = byDate.get(r.date) ?? [];
    arr.push(r);
    byDate.set(r.date, arr);
  }

  const out: DailyChartRow[] = [];
  for (let day = 1; day <= total; day++) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
      2,
      "0",
    )}`;
    const dayRows = byDate.get(iso) ?? [];
    const byCore: DailyCoreBreakdown = {};
    for (const r of dayRows) {
      byCore[r.model_core] = {
        requests: r.requests,
        tokens: r.tokens,
        credits: r.credits,
      };
    }

    const row: DailyChartRow = {
      date: iso,
      dayLabel: String(day),
      _byCore: byCore,
    };
    for (const core of cores) {
      row[core] = byCore[core]?.credits ?? 0;
    }
    out.push(row);
  }
  return out;
}
